import { useState, useEffect, useRef } from 'react'
import React from 'react'
import { SearchIcon, ArrowRightIcon, LoadingIcon, CloseIcon } from '../icons'
import { clsx } from 'clsx'
import { TypographyCaps, TypographyLabel, TypographyParagraph } from '../typography'
import InternalLink from '../common/internalLink'
import ContentTile from '../content/ContentTile'
import Button from '../buttons/button'
import { useNavigate } from '@remix-run/react'

// TODO: Define a proper type for search results
type SearchResultItem = {
	id: string
	type: 'user' | 'collection' | 'event' | 'nft' // Category type
	name: string
	description: string
	image?: string
	url: string
	isVerified?: boolean
}

// TODO: Structure results by category
type SearchResults = {
	users: SearchResultItem[]
	collections: SearchResultItem[]
	events: SearchResultItem[]
	nfts: SearchResultItem[]
}

// Use the new API endpoint for search
const fetchSearchResults = async (query: string): Promise<SearchResults> => {
	try {
		const response = await fetch('/searchAPI', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ string: query }),
		});

		if (!response.ok) {
			throw new Error(`Search API request failed with status: ${response.status}`);
		}

		const data = await response.json();
		return data;
	} catch (error) {
		console.error('Error fetching search results:', error);
		
		// Return empty results on error
		return { users: [], collections: [], events: [], nfts: [] };
	}
}

// TODO: might add this as an env variable before adding to some backend endpoint
interface TrendingSearch {
	name: string;
	URL: string;
}

const TRENDING_SEARCHES = JSON.parse(import.meta.env.VITE_SEARCH_TRENDING_ITEMS) ||
	[
		{name: 'Bryan Brinkman', URL: 'https://via.placeholder.com/150'},
		{name: 'XCOPY', URL: 'https://via.placeholder.com/150'},
		{name: 'The Monument Game', URL: 'https://via.placeholder.com/150'},
		{name: 'Masquerade', URL: 'https://via.placeholder.com/150'}
	] as TrendingSearch[]

const SearchBar = (
	{
		isInactive = false
	}: {
		isInactive?: boolean
	}
) => {
	const [query, setQuery] = useState('')
	const [isDropdownOpen, setIsDropdownOpen] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [searchResults, setSearchResults] = useState<SearchResults | null>(null)
	const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
	const dropdownRef = useRef<HTMLDivElement>(null)
	const isClickingDropdown = useRef(false)

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const currentQuery = e.target.value
		setQuery(currentQuery)
		setSearchResults(null)

		// Clear existing timeout if there is one
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current)
		}

		if (currentQuery.length >= 3) {
			setIsLoading(true)
			setIsDropdownOpen(true)
			
			// Set new timeout for the search
			searchTimeoutRef.current = setTimeout(async () => {
				try {
					const results = await fetchSearchResults(currentQuery)
					setSearchResults(results)
				} catch (error) {
					console.error("Search failed:", error)
					setSearchResults(null)
				} finally {
					setIsLoading(false)
				}
			}, 1000) // 1 second debounce
		} else {
			setIsLoading(false)
			setSearchResults(null)
		}
	}

	// Clean up the timeout when component unmounts
	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}
		}
	}, [])

	const handleFocus = () => {
		// First ensure we're not loading to avoid flicker
		if (!isLoading) {
			setIsDropdownOpen(true)
		}
	}
	
	const clearSearch = () => {
		setQuery('')
		setSearchResults(null)
		setIsDropdownOpen(false)
	}

	// Handle mouse events on dropdown to prevent onBlur from closing it during clicks
	const handleMouseDown = () => {
		isClickingDropdown.current = true
	}

	const handleMouseUp = () => {
		setTimeout(() => {
			isClickingDropdown.current = false
		}, 100) // Small delay to ensure click event is processed
	}

	const handleBlur = () => {
		if (!isClickingDropdown.current) {
			setIsDropdownOpen(false)
		}
	}

	return (
		<div className="flex-1 sm:max-w-[566px] xs:max-w-[360px] max-w-[222px] mx-auto">
			<div className="">
				<div className="relative group">
					<div
						className={clsx(
							'pointer-events-none absolute inset-y-0 left-0 flex items-center p-2',
							{
								'text-textInactiveDark dark:text-textInactiveLight': isInactive,
								'text-textSecondaryDark group-hover:text-textSecondaryLight group-hover:dark:text-textSecondaryDark': !isInactive,
								'group-focus:dark:text-textPrimaryLight group-focus:text-textPrimaryDark': !isInactive && query !== '',
								'text-textSecondaryDark dark:text-textSecondaryLight': !isInactive && query === '' && !isLoading
							}
						)}
					>
						<SearchIcon />
					</div>
					
					{/* Right side icons (loading spinner or close icon) */}
					{(isLoading || query !== '') && (
						<div className={clsx(
							'absolute inset-y-0 right-0 flex items-center p-2',
							isLoading ? 'pointer-events-none' : 'cursor-pointer'
						)}>
							{isLoading ? (
								<LoadingIcon className="text-textSecondaryLight dark:text-textSecondaryDark" />
							) : query !== '' && (
								<div onClick={clearSearch}>
									<CloseIcon className={clsx(
										'text-textSecondaryLight dark:text-textSecondaryDark',
										{
											'hover:dark:text-textPrimaryLight hover:text-textPrimaryDark': isDropdownOpen,
											'hover:dark:text-textSecondaryLight hover:text-textSecondaryDark': !isDropdownOpen
										}
									)} />
								</div>
							)}
						</div>
					)}
					
					<input
						type="text"
						value={query}
						onChange={handleInputChange}
						onFocus={handleFocus}
						onBlur={handleBlur}
						className={clsx(
							// Base styles
							'cursor-pointer py-3 font-abc text-paragraphMedium max-h-10 block w-full rounded-md border-0 pl-10 outline-0 ring-0 focus:outline-0 focus:ring-0 bg-uiTertiaryLight dark:bg-uiSecondaryDark',
							
							// Right padding based on content
							(isLoading || query !== '') && 'pr-10',
							
							// Inactive state
							isInactive && 'cursor-not-allowed bg-uiTertiaryLight text-textSecondaryDark placeholder:text-textSecondaryDark dark:bg-uiPrimaryLight dark:text-textSecondaryDark dark:placeholder:text-textSecondaryDark',
							
							// Active states (only apply if not inactive)
							!isInactive && {
								// Empty search field
								'bg-uiTertiaryLight dark:bg-uiSecondaryDark': query === '',
								
								// Dropdown closed
								'placeholder:text-textSecondaryLight dark:placeholder:text-textSecondaryDark': !isDropdownOpen,
								
								// Focused state
								'focus:bg-uiPrimaryDark focus:dark:bg-uiPrimaryLight focus:text-textPrimaryDark focus:dark:text-textPrimaryLight': true,
								
								// Text field with content and dropdown open
								'text-textPrimaryLight dark:text-textPrimaryDark': query !== '',
								
								// Placeholder text
								'placeholder:text-textSecondaryDark dark:placeholder:text-textSecondaryLight': true
							}
						)}
						placeholder="Search Lore"
						disabled={isInactive}
					/>
				</div>

				{/* Dropdown */}
				<div className="relative">
					<SearchDropDown
						setQuery={setQuery}
						setIsDropdownOpen={setIsDropdownOpen}
						isLoading={isLoading}
						searchResults={searchResults}
						query={query}
						isOpen={isDropdownOpen && !isLoading}
						ref={dropdownRef}
						onMouseDown={handleMouseDown}
						onMouseUp={handleMouseUp}
					/>
				</div>
			</div>
		</div>
	)
}

const SearchDropDown = React.forwardRef(({
	setQuery,
	setIsDropdownOpen,
	isLoading,
	searchResults,
	query,
	isOpen,
	onMouseDown,
	onMouseUp
}: {
	setQuery: (value: string) => void
	setIsDropdownOpen: (value: boolean) => void
	isLoading: boolean
	searchResults: SearchResults | null
	query: string
	isOpen: boolean
	onMouseDown: () => void
	onMouseUp: () => void
}, ref: React.ForwardedRef<HTMLDivElement>): JSX.Element => {
	const navigate = useNavigate();
	
	const hasResults = searchResults && (
		searchResults.users.length > 0 ||
		searchResults.collections.length > 0 ||
		searchResults.events.length > 0 ||
		searchResults.nfts.length > 0
	)

	const renderContentTiles = (items: SearchResultItem[]) => {
		return items.map((item, index) => (
			<div key={`${item.type}-${index}`} onClick={() => setIsDropdownOpen(false)}>
				<InternalLink 
					to={item.url} 
					additionalClasses='block' 
				>
					<ContentTile
						title={item.name}
						description={item.description}
						image={item.image}
						variant={item.type === 'user' ? 'user' : 'collection'}
						isVerified={item.isVerified}
						showBorder={false}
					/>
				</InternalLink>
			</div>
		))
	}

	return (
		<div 
			ref={ref}
			onMouseDown={onMouseDown}
			onMouseUp={onMouseUp}
			className={clsx(
				"absolute z-50 w-full mt-2 bg-uiTertiaryLight dark:bg-grey900 rounded-md shadow-lg border border-uiPrimaryLight dark:border-uiSecondaryDark flex flex-col max-h-[70vh] transition-all duration-300 ease-in-out transform origin-top",
				isOpen ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
			)}
			style={{ willChange: 'transform' }}
		>

			{/* Scrollable Content Area - Added scrollbar hiding classes */}
			<div className="flex-grow overflow-y-auto p-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
				{isLoading && (
					<div className="flex items-center justify-center p-4">
						<LoadingIcon className="text-textSecondaryLight dark:text-textSecondaryDark mr-3" />
						<TypographyLabel size="small" additionalClasses="text-textSecondaryDark dark:text-textSecondaryLight">
							Searching...
						</TypographyLabel>
					</div>
				)}

				{/* Showing results based on search query */}
				{!isLoading && searchResults && hasResults && (
					<div className="flex flex-col gap-y-2 transition-transform duration-300 ease-in-out">
						<TypographyParagraph size="medium" isBold additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark p-2">
							Showing results for "{query}"
						</TypographyParagraph>
						{searchResults.users.length > 0 && (
							<div>
								<TypographyCaps size="small" additionalClasses="text-textSecondaryDark dark:text-textSecondaryLight p-2">
									Users
								</TypographyCaps>
								<div className="flex flex-col gap-y-1">
									{renderContentTiles(searchResults.users)}
								</div>
							</div>
						)}
						{searchResults.collections.length > 0 && (
							<div>
								<TypographyCaps size="small" additionalClasses="text-textSecondaryDark dark:text-textSecondaryLight p-2">
									Collections
								</TypographyCaps>
								<div className="flex flex-col gap-y-1">
									{renderContentTiles(searchResults.collections)}
								</div>
							</div>
						)}
						{searchResults.events.length > 0 && (
							<div>
								<TypographyCaps size="small" additionalClasses="text-textSecondaryDark dark:text-textSecondaryLight p-2">
									Events
								</TypographyCaps>
								<div className="flex flex-col gap-y-1">
									{renderContentTiles(searchResults.events)}
								</div>
							</div>
						)}
						{searchResults.nfts.length > 0 && (
							<div>
								<TypographyCaps size="small" additionalClasses="text-textSecondaryDark dark:text-textSecondaryLight p-2">
									NFTs
								</TypographyCaps>
								<div className="flex flex-col gap-y-1">
									{renderContentTiles(searchResults.nfts)}
								</div>
							</div>
						)}
					</div>
				)}


				{/* Showing trending searches - Use query length */}
				{!isLoading && query.length < 3 && !hasResults && (
					<div className="transition-transform duration-300 ease-in-out">
						<TypographyCaps
							size="small"
							additionalClasses="text-textSecondaryDark dark:text-textSecondaryLight p-2"
						>
							TRENDING SEARCHES
						</TypographyCaps>
						<div className="py-4 flex flex-col gap-y-2">
							{TRENDING_SEARCHES.map((search: TrendingSearch, index: number) => (
								<InternalLink
									key={String(index)}
									additionalClasses="w-full text-left px-2 py-1 text-sm text-uiPrimaryLight rounded-md hover:bg-uiPrimaryLight dark:hover:bg-uiPrimaryDark"
									to={search.URL}
								>
									<TypographyLabel
										size="small"
										additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark flex items-center gap-x-1 "
									>
										{search.name}
									</TypographyLabel>
								</InternalLink>
							))}
						</div>
					</div>
				)}

				{/* No results Dropdown */}
				{!isLoading && searchResults && !hasResults && (
					<div className="flex flex-col gap-y-2 transition-transform duration-300 ease-in-out">
						<TypographyCaps size="small" additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark p-2">
							No results for "{query}"
						</TypographyCaps>
						<div className="py-4 text-center flex flex-col items-center gap-y-4 gap-x-2">
							<div className="flex flex-col items-center gap-y-2">
								<TypographyParagraph size="medium" additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark">
									My NFT is not in LORE yet.
								</TypographyParagraph>
								<TypographyParagraph size="small" additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark">
									Add your NFT to Lore to start building its legacy
								</TypographyParagraph>
							</div>
							<Button
								variant="primary"
								additionalClasses="w-auto flex items-center whitespace-nowrap"
								rightIcon={<ArrowRightIcon size="small" />}
								onClick={() => {
									setIsDropdownOpen(false);
									navigate('/FindNFT');
								}}
							>
								Find NFT
							</Button>
						</div>
					</div>
				)}
			</div>

			{/* CTA Section - Fixed Footer */}
			{!isLoading && (query.length < 3 || hasResults) && (
				<div className="p-4 border-t border-uiPrimaryLight dark:border-uiSecondaryDark shrink-0 transition-opacity duration-300 ease-in-out">
					<TypographyParagraph
						size="small"
						additionalClasses="w-full text-left text-sm text-textSecondaryLight dark:text-textSecondaryDark rounded-md flex items-center"
					>
						Didn't find what you're looking for?
						<div onClick={() => setIsDropdownOpen(false)}>
							<InternalLink
								additionalClasses="ml-2 text-textPrimaryLight dark:text-textPrimaryDark flex items-center gap-x-1 hover:underline"
								to="/FindNFT"
							>
								Find NFT <ArrowRightIcon size="small" />
							</InternalLink>
						</div>
					</TypographyParagraph>
				</div>
			)}
		</div>
	)
});

export default SearchBar
